const CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const MAX_CACHE_ENTRIES = 250;

const autocompleteCache = new Map();

const normalizeText = value =>
  String(value || '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLocaleLowerCase();

const getRowKey = row => row?.id ?? row?.row_id;

export const getPreviousAiItemLabels = ({ rows, currentRow, rowIndex }) => {
  const currentRowKey = String(getRowKey(currentRow) ?? '');
  const matchingIndex = currentRowKey
    ? (rows || []).findIndex(
        row => String(getRowKey(row) ?? '') === currentRowKey
      )
    : -1;
  const endIndex = matchingIndex >= 0 ? matchingIndex : Math.max(0, rowIndex);
  const seen = new Set();

  return (rows || [])
    .slice(0, endIndex)
    .map(row =>
      String(row?.item || '')
        .replace(/\s+/g, ' ')
        .trim()
    )
    .filter(item => {
      const normalized = normalizeText(item);
      if (!normalized || seen.has(normalized)) return false;
      seen.add(normalized);
      return true;
    })
    .slice(-20);
};

export const buildAiAutocompleteScopeKey = ({
  budgetId,
  categoryId,
  previousItems,
}) => {
  const detailItems = (previousItems || []).map(normalizeText).filter(Boolean);

  return `${budgetId || ''}:${categoryId || ''}:${detailItems.join('\u001f')}`;
};

const deleteExpiredEntries = now => {
  autocompleteCache.forEach((entry, key) => {
    if (entry.expiresAt <= now) autocompleteCache.delete(key);
  });
};

const makeCacheKey = (scopeKey, query) =>
  `${scopeKey}\u001e${normalizeText(query)}`;

export const storeAiAutocompleteSuggestions = (
  scopeKey,
  query,
  suggestions,
  now = Date.now()
) => {
  const normalizedQuery = normalizeText(query);
  if (!scopeKey) return;

  deleteExpiredEntries(now);
  const key = makeCacheKey(scopeKey, normalizedQuery);
  autocompleteCache.delete(key);
  autocompleteCache.set(key, {
    scopeKey,
    query: normalizedQuery,
    suggestions: Array.isArray(suggestions) ? suggestions : [],
    expiresAt: now + CACHE_TTL_MS,
  });

  while (autocompleteCache.size > MAX_CACHE_ENTRIES) {
    autocompleteCache.delete(autocompleteCache.keys().next().value);
  }
};

export const getAiAutocompleteSuggestions = (
  scopeKey,
  query,
  now = Date.now()
) => {
  const normalizedQuery = normalizeText(query);
  if (!scopeKey) return null;

  deleteExpiredEntries(now);
  const exactEntry = autocompleteCache.get(
    makeCacheKey(scopeKey, normalizedQuery)
  );
  if (exactEntry) return exactEntry.suggestions;

  if (normalizedQuery.length < 2) return null;

  const prefixEntries = [...autocompleteCache.values()]
    .filter(
      entry =>
        entry.scopeKey === scopeKey &&
        normalizedQuery.startsWith(entry.query) &&
        entry.query.length < normalizedQuery.length
    )
    .sort((left, right) => right.query.length - left.query.length);

  for (const entry of prefixEntries) {
    const filtered = entry.suggestions.filter(suggestion =>
      normalizeText(suggestion?.label).startsWith(normalizedQuery)
    );
    if (filtered.length) return filtered;
  }

  return null;
};

export const clearAiAutocompleteCache = () => {
  autocompleteCache.clear();
};
